(() => {
  const SELECTORS = {
    dialog: '[data-kbd-daily-updates-dialog]',
    open: '[data-kbd-daily-updates-open]',
    close: '[data-kbd-daily-updates-close]',
    announcement: '[data-kbd-daily-announcement]'
  };

  const ANNOUNCEMENT_ENDPOINT = '/apps/kbd-notify/daily-announcement';
  const DAILY_FEED_ENDPOINT = '/apps/kbd-notify/daily-feed';
  const CATEGORY_LABELS = {
    new: 'NEW ARRIVAL',
    restock: 'RESTOCKED',
    sale: 'ON SALE'
  };

  const setText = (root, selector, value) => {
    const element = root.querySelector(selector);
    if (element) element.textContent = value || '';
  };

  const loadAnnouncement = async dialog => {
    const announcement = dialog?.querySelector(SELECTORS.announcement);
    if (!announcement || announcement.dataset.kbdDailyAnnouncementLoading === 'true') return;
    announcement.dataset.kbdDailyAnnouncementLoading = 'true';
    try {
      const response = await fetch(ANNOUNCEMENT_ENDPOINT, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Announcement request failed: ${response.status}`);
      const data = await response.json();
      const visible = data.enabled === true && Boolean(data.title || data.body);
      announcement.hidden = !visible;
      if (!visible) return;

      setText(announcement, '[data-kbd-daily-announcement-eyebrow]', data.eyebrow || 'Announcement');
      setText(announcement, '[data-kbd-daily-announcement-title]', data.title);
      setText(announcement, '[data-kbd-daily-announcement-body]', data.body);
      const link = announcement.querySelector('[data-kbd-daily-announcement-link]');
      if (link) {
        const rawUrl = String(data.linkUrl || '').trim();
        let safeUrl = '';
        try {
          const parsed = new URL(rawUrl, window.location.origin);
          if (['http:', 'https:'].includes(parsed.protocol)) safeUrl = parsed.href;
        } catch (_) {
          safeUrl = '';
        }
        link.hidden = !safeUrl;
        if (safeUrl) link.href = safeUrl;
        else link.removeAttribute('href');
        setText(link, '[data-kbd-daily-announcement-link-text]', data.linkText || 'Learn more');
      }
    } catch (_) {
      announcement.hidden = true;
    } finally {
      announcement.dataset.kbdDailyAnnouncementLoading = 'false';
    }
  };

  const formatMoney = (amount, currencyCode) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(value);
    } catch (_) {
      return `$${value.toFixed(2)}`;
    }
  };

  const createProductCard = (product, category) => {
    const card = document.createElement('a');
    card.className = 'kbd-daily-updates-card';
    card.href = `/products/${encodeURIComponent(product.handle || '')}`;
    card.dataset.kbdDailyProductId = product.id || '';
    card.dataset.kbdDailyManual = 'true';

    const media = document.createElement('span');
    media.className = 'kbd-daily-updates-card__media';
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.imageAlt || product.title || '';
      image.loading = 'lazy';
      image.width = 90;
      image.height = 90;
      media.append(image);
    }

    const content = document.createElement('span');
    content.className = 'kbd-daily-updates-card__content';
    const meta = document.createElement('span');
    meta.className = 'kbd-daily-updates-card__meta';
    meta.textContent = CATEGORY_LABELS[category] || '';
    const title = document.createElement('span');
    title.className = 'kbd-daily-updates-card__title';
    title.textContent = product.title || '';
    const price = document.createElement('span');
    price.className = 'kbd-daily-updates-card__price';
    const currentPrice = document.createElement('span');
    currentPrice.textContent = formatMoney(product.price, product.currencyCode);
    price.append(currentPrice);
    if (category === 'sale' && Number(product.compareAtPrice) > Number(product.price)) {
      const compareAt = document.createElement('s');
      compareAt.textContent = formatMoney(product.compareAtPrice, product.currencyCode);
      price.append(compareAt);
    }
    content.append(meta, title, price);

    const arrow = document.createElement('span');
    arrow.className = 'kbd-daily-updates-card__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    card.append(media, content, arrow);
    return card;
  };

  const applyDailyFeed = (dialog, categories) => {
    Object.entries(categories || {}).forEach(([category, config]) => {
      const group = dialog.querySelector(`[data-kbd-daily-group="${category}"]`);
      const list = group?.querySelector('[data-kbd-daily-list]');
      if (!group || !list) return;

      list.querySelectorAll('[data-kbd-daily-manual]').forEach(card => card.remove());
      const pinned = Array.isArray(config?.pinned) ? config.pinned : [];
      const pinnedIds = new Set(pinned.map(({ id }) => id));
      const hiddenIds = new Set((config?.hidden || []).map(({ id }) => id));
      list.querySelectorAll('[data-kbd-daily-auto]').forEach(card => {
        card.hidden = hiddenIds.has(card.dataset.kbdDailyProductId) || pinnedIds.has(card.dataset.kbdDailyProductId);
      });

      const fragment = document.createDocumentFragment();
      pinned.forEach(product => fragment.append(createProductCard(product, category)));
      list.prepend(fragment);

      const visibleCards = [...list.querySelectorAll('.kbd-daily-updates-card')].filter(card => !card.hidden);
      group.hidden = !visibleCards.length;
    });

    const hasContent = [...dialog.querySelectorAll('[data-kbd-daily-group]')].some(group => !group.hidden) ||
      Boolean(dialog.querySelector('.kbd-daily-updates__group:not([data-kbd-daily-group])'));
    const empty = dialog.querySelector('[data-kbd-daily-empty]');
    if (empty) empty.hidden = hasContent;
  };

  const loadDailyFeed = async dialog => {
    if (!dialog || dialog.dataset.kbdDailyFeedLoading === 'true') return;
    dialog.dataset.kbdDailyFeedLoading = 'true';
    try {
      const response = await fetch(DAILY_FEED_ENDPOINT, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error(`Daily feed request failed: ${response.status}`);
      const data = await response.json();
      applyDailyFeed(dialog, data.categories);
    } catch (_) {
      // Keep the server-rendered automatic feed when KBD TOOL is unavailable.
    } finally {
      dialog.dataset.kbdDailyFeedLoading = 'false';
    }
  };

  const setExpanded = value => {
    document.querySelectorAll(SELECTORS.open).forEach(button => {
      button.setAttribute('aria-expanded', String(value));
    });
  };

  const closeDialog = dialog => {
    if (!dialog) return;
    if (typeof dialog.close === 'function') dialog.close();
    else dialog.removeAttribute('open');
    document.documentElement.classList.remove('kbd-daily-updates-open');
    setExpanded(false);
  };

  const openDialog = dialog => {
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    document.documentElement.classList.add('kbd-daily-updates-open');
    setExpanded(true);
    dialog.querySelector(SELECTORS.close)?.focus({ preventScroll: true });
    loadAnnouncement(dialog);
  };

  const init = () => {
    const dialog = document.querySelector(SELECTORS.dialog);
    if (!dialog) return;
    loadAnnouncement(dialog);
    loadDailyFeed(dialog);

    document.querySelectorAll(SELECTORS.open).forEach(button => {
      if (button.dataset.kbdDailyUpdatesBound === 'true') return;
      button.dataset.kbdDailyUpdatesBound = 'true';
      button.addEventListener('click', () => openDialog(dialog));
    });

    dialog.querySelectorAll(SELECTORS.close).forEach(button => {
      if (button.dataset.kbdDailyUpdatesBound === 'true') return;
      button.dataset.kbdDailyUpdatesBound = 'true';
      button.addEventListener('click', () => closeDialog(dialog));
    });

    dialog.querySelectorAll('a[href]').forEach(link => {
      if (link.dataset.kbdDailyUpdatesLinkBound === 'true') return;
      link.dataset.kbdDailyUpdatesLinkBound = 'true';
      link.addEventListener('click', event => {
        event.stopPropagation();
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        window.location.assign(link.href);
      });
    });

    if (dialog.dataset.kbdDailyUpdatesBound !== 'true') {
      dialog.dataset.kbdDailyUpdatesBound = 'true';
      dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('kbd-daily-updates-open');
        setExpanded(false);
      });
      dialog.addEventListener('click', event => {
        const bounds = dialog.getBoundingClientRect();
        const outside = event.clientX < bounds.left || event.clientX > bounds.right ||
          event.clientY < bounds.top || event.clientY > bounds.bottom;
        if (outside) closeDialog(dialog);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
